import * as React from 'react';
import type { IUserJSON } from 'external-api-types';
import HybridLink from '../../common/HybridLink/HybridLink';
import ServiceIcon from '../../common/ServiceIcon/ServiceIcon';
import Button, { HybridLinkButton } from '../../common/Button/Button';
import Header from '../Header';
import './error-page.scss';

type CommonProps = {
    onDismiss?: () => void;
};

const GeneralError = (props: CommonProps & { error: string; action: 'refresh' | 'dashboard' }) => (
    <div className="error-container">
        <h1 className="error-title">Error</h1>
        <p className="error-desc">{props.error}</p>
        {props.action === 'refresh' && (
            <Button className="error-action-button" type="button" buttonType="secondary" onClick={() => window.location.reload()}>
                Refresh Page
            </Button>
        )}
        {props.action === 'dashboard' && (
            <HybridLink className="error-action-button" to="/dashboard" onClick={props.onDismiss}>
                Back to Dashboard
            </HybridLink>
        )}
    </div>
);

const Crash = () => (
    <div className="error-container">
        <h1 className="error-title">Oops! Something went wrong.</h1>
        <p className="error-desc">We apologize, our team will investigate this error.</p>
        <Button className="error-action-button" type="button" buttonType="secondary" onClick={() => window.location.reload()}>
            Refresh Page
        </Button>
    </div>
);

const NoGithubError = () => {
    const currentUrl = window.location.pathname + window.location.search;
    const cmsConnectionUrl = `/quick-login?provider=github&redirect=${currentUrl}`;
    return (
        <div className="error-container">
            <h1 className="error-title">No GitHub Connection</h1>
            <p className="error-desc">Netlify Create has lost connection to your GitHub account. Please reconnect GitHub in order to proceed.</p>
            <HybridLinkButton
                to={cmsConnectionUrl}
                className="error-action-button"
                buttonType="secondary"
                icon={<ServiceIcon serviceId="github" type="deployment" className="connect-cta-icon" />}
                onClick={() => window.location.reload()}
            >
                Connect GitHub
            </HybridLinkButton>
        </div>
    );
};

const NoGithubErrorCollaborator = () => (
    <div className="error-container">
        <h1 className="error-title">No GitHub Connection</h1>
        <p className="error-desc">Netlify Create has lost connection to GitHub account. Please contact project owner to proceed.</p>
    </div>
);

export type ErrorPageError = 'NO_PROJECT_FOUND' | 'NO_GITHUB' | 'NO_GITHUB_COLLABORATOR' | 'CRASH';

interface ErrorPageProps extends CommonProps {
    error: ErrorPageError | string;
    user: IUserJSON | null;
}

const ErrorPage = (props: ErrorPageProps): JSX.Element => {
    const { error } = props;

    let contents;

    if (error === 'NO_PROJECT_FOUND') {
        contents = <GeneralError action="dashboard" error="No Project Found" onDismiss={props.onDismiss} />;
    } else if (error === 'NO_GITHUB') {
        contents = <NoGithubError />;
    } else if (error === 'NO_GITHUB_COLLABORATOR') {
        contents = <NoGithubErrorCollaborator />;
    } else {
        contents = <Crash />;
    }

    return (
        <div className="page page-error">
            <Header user={props.user} />
            {contents}
        </div>
    );
};

export default ErrorPage;
